import {
  Box,
  Collapse,
  IconButton,
  InputLabel,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Checkbox,
  List,
  Divider,
} from "@mui/material";
import React, { memo, useState } from "react";
import { FormikProps, useFormikContext } from "formik";
import { Data } from "../../../store/usergroup/types";
import { set, get } from "lodash";
import { Folder, ExpandMore, ExpandLess } from "@mui/icons-material";

const CREATE = "C";
const UPDATE = "U";
const DELETE = "D";
const READ = "R";

interface Props {
  permissionKey: string;
  permissions: any;
}

const classes = {
  txt: {
    color: "var(--dmg-primary)",
    textDecoration: "none",
    fontWeight: "bold",
    paddingRight: "0",
  },
  icon: { color: "var(--dmg-primary)" },
  isChild: {
    paddingLeft: 2,
  },
};

const PermissionForm = (props: Props) => {
  const [openSettings, setOpenSettings] = useState(true);
  const { permissionKey, permissions } = props;
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;
  const isBulkUpdate = permissionKey === "BulkUpdate";

  const onChangePermission = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const permission = get(values, `attributes.Permission[${name}]`, {});
    const checked = !get(permission, `Access.R`);

    set(permission, `Access.R`, checked);
    if (!checked) {
      set(permission, `Access.U`, checked);
      set(permission, `Access.D`, isBulkUpdate ? false : checked);
      set(permission, `Access.C`, isBulkUpdate ? false : checked);
    }
    const SectionAccess = permissions[permissionKey].SectionAccess || null;
    if (SectionAccess) {
      Object.keys(SectionAccess).map((key) => {
        if (!checked) {
          set(permission, `SectionAccess.${key}.Access.U`, checked);
        }
        set(permission, `SectionAccess.${key}.Access.R`, checked);
      });
    }
    setFieldValue(`attributes.Permission[${name}]`, permission);
  };

  const onChangeUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const permission = get(values, `attributes.Permission[${name}]`, {});
    const checked = !get(permission, `Access.U`);

    set(permission, `Access.U`, checked);
    const SectionAccess = permissions[permissionKey].SectionAccess || null;
    if (SectionAccess) {
      Object.keys(SectionAccess).map((key) => {
        set(permission, `SectionAccess.${key}.Access.U`, checked);
      });
    }
    setFieldValue(`attributes.Permission[${name}]`, permission);
  };

  const onChangeAccessPermission = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name } = event.target;
    setFieldValue(name, !get(values, name));
  };

  const renderPermissionKey = (key: string) => {
    if (key) {
      return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    }
    return "";
  };

  const access = get(
    values,
    `attributes.Permission.${permissionKey}.Access`,
    {}
  );

  const SectionAccess = permissions[permissionKey].SectionAccess || null;
  let indeterminateR = false;
  let indeterminateU = false;
  if (SectionAccess) {
    const childAccess = get(
      values,
      `attributes.Permission.${permissionKey}.SectionAccess`,
      {}
    );
    indeterminateR = Object.entries(childAccess)
      .map(([key, obj]) => obj)
      .some((obj) => !get(obj, "Access.R", false));
    indeterminateU = Object.entries(childAccess)
      .map(([key, obj]) => obj)
      .some((obj) => !get(obj, "Access.U", false));
  }

  return (
    <Box
      key={permissionKey}
      sx={{ p: 2 }}
      style={{
        width: "100%",
        border: "1px solid #efefef",
        marginBottom: "10px",
        paddingRight: "10px",
      }}
    >
      <ListItem sx={classes.txt}>
        <ListItemIcon>
          <Folder color="primary"></Folder>
        </ListItemIcon>
        <ListItemText
          primary={renderPermissionKey(
            permissionKey === "UserGroup" ? "Role" : permissionKey
          )}
        />
        <ListItemSecondaryAction>
          <Box display="flex" justifyContent="flex-end">
            <Box
              flexGrow={1}
              width={65}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <InputLabel shrink>Read</InputLabel>
              <Checkbox
                indeterminate={indeterminateR && access.R}
                checked={Boolean(access.R)}
                name={permissionKey}
                onChange={onChangePermission}
              />
            </Box>
            <Box
              width={65}
              flexGrow={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <InputLabel shrink>Update</InputLabel>
              <Checkbox
                indeterminate={indeterminateU && access.U}
                checked={Boolean(access.U)}
                name={permissionKey}
                disabled={!access.R}
                onChange={onChangeUpdate}
              />
            </Box>
            <Box
              width={65}
              flexGrow={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <InputLabel shrink>Create</InputLabel>
              <Checkbox
                checked={Boolean(access.C)}
                name={`attributes.Permission.${permissionKey}.Access.C`}
                disabled={!access.R || isBulkUpdate}
                onChange={onChangeAccessPermission}
              />
            </Box>
            <Box
              width={65}
              flexGrow={1}
              mr={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <InputLabel color="primary" shrink>
                Delete
              </InputLabel>
              <Checkbox
                checked={Boolean(access.D)}
                name={`attributes.Permission.${permissionKey}.Access.D`}
                disabled={!access.R || isBulkUpdate}
                onChange={onChangeAccessPermission}
              />
            </Box>
          </Box>
        </ListItemSecondaryAction>
        {SectionAccess && (
          <IconButton
            type="button"
            onClick={() => setOpenSettings(!openSettings)}
            edge="start"
          >
            {openSettings ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItem>
      {SectionAccess && (
        <Collapse in={openSettings} timeout="auto" unmountOnExit>
          <Divider />
          <List
            style={{ marginTop: "10px", paddingLeft: "30px" }}
            component="div"
            disablePadding
          >
            {Object.keys(SectionAccess || {}).map((sectionKey: string, idx) => {
              const childAccess = get(
                values,
                `attributes.Permission.${permissionKey}.SectionAccess.${sectionKey}.Access`,
                {}
              );
              return (
                <ListItem
                  style={{
                    borderBottom:
                      idx < Object.keys(SectionAccess || {}).length - 1
                        ? "1px solid #efefef"
                        : "",
                  }}
                  key={sectionKey}
                  sx={classes.isChild}
                >
                  <ListItemIcon>
                    <Folder color="secondary"></Folder>
                  </ListItemIcon>
                  <ListItemText primary={renderPermissionKey(sectionKey)} />
                  <ListItemSecondaryAction style={{ marginRight: "130px" }}>
                    <Box display="flex" justifyContent="flex-end">
                      <Box
                        width={65}
                        flexGrow={1}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Checkbox
                          color="secondary"
                          disabled={!access.R}
                          checked={Boolean(childAccess.R)}
                          name={`attributes.Permission.${permissionKey}.SectionAccess.${sectionKey}.Access.R`}
                          onChange={onChangeAccessPermission}
                        />
                      </Box>
                      <Box
                        width={65}
                        flexGrow={1}
                        mr={2}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Checkbox
                          color="secondary"
                          name={`attributes.Permission.${permissionKey}.SectionAccess.${sectionKey}.Access.U`}
                          checked={Boolean(childAccess.U)}
                          disabled={!access.R}
                          onChange={onChangeAccessPermission}
                        />
                      </Box>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </Box>
  );
};

export default PermissionForm;
